'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { placeTip, type TipAnchor, type TipPlacement } from '@content-removal/shared';

/* Measured at its natural width, then moved. Put at `left: centre` and pulled
   back with a transform, it wrapped into the few pixels left at the window's
   edge — the browser wraps where the box was put. `placeTip` places it. */
export type { TipAnchor };

/** One line of the wrapped text: where it starts, where it ends. */
interface Line {
  left: number;
  right: number;
}

/* The lines the text is actually on. A Range reports one rectangle per inline
   box, not per line — the bold opening sentence and the rest are two of them,
   often on the same line — so rectangles are grouped by the line they sit on
   before anything is measured. Reading them as lines was what made the old
   measurement come out short: it took the widest rectangle, which is a piece
   of a line, and sized the card to that. */
function linesOf(node: HTMLElement): Line[] {
  const range = document.createRange();
  range.selectNodeContents(node);
  const lines: Array<Line & { top: number }> = [];
  for (const rect of Array.from(range.getClientRects())) {
    if (rect.width < 0.5 || rect.height < 0.5) continue;
    // Within a couple of pixels is the same line: a bold run and the text
    // after it sit on the same baseline but report tops a hair apart.
    const line = lines.find((one) => Math.abs(one.top - rect.top) <= 2);
    if (line) {
      line.left = Math.min(line.left, rect.left);
      line.right = Math.max(line.right, rect.right);
    } else {
      lines.push({ top: rect.top, left: rect.left, right: rect.right });
    }
  }
  return lines;
}

/** What the card adds around its text: padding and border, left and right. */
function frameOf(node: HTMLElement): number {
  const style = getComputedStyle(node);
  return (
    parseFloat(style.paddingLeft)
    + parseFloat(style.paddingRight)
    + parseFloat(style.borderLeftWidth)
    + parseFloat(style.borderRightWidth)
  );
}

/* The floor of the search. Narrower than this and a sentence reads as a
   column of two words; the ceiling is the stylesheet's, in characters. */
const NARROWEST_PX = 150;
/* Two pixels apart is the same answer; eight halvings reach that from any cap
   this card can have. Each one costs a layout, and there is one card. */
const CLOSE_ENOUGH_PX = 2;
const TRIES = 8;

/* The narrowest the card can be without costing the text a line. Measured as
   lines rather than as the inline boxes a Range reports, and searched with the
   card wrapping as it will be read: sizing a box to lines it then re-wraps is
   what left air beside the last one. */
function fitToText(node: HTMLElement): void {
  const frame = frameOf(node);
  const atCap = linesOf(node);
  if (atCap.length === 0) return;

  const widthOf = (lines: Line[]) => Math.max(...lines.map((line) => line.right - line.left));
  const set = (content: number) => {
    node.style.width = `${Math.ceil(content + frame)}px`;
    return linesOf(node);
  };

  // One line: it is as wide as its text and there is nothing to search for.
  if (atCap.length === 1) {
    set(widthOf(atCap));
    return;
  }

  let tooNarrow = NARROWEST_PX - 1;
  let fits = widthOf(atCap);
  let fitting = atCap;
  for (let i = 0; i < TRIES && fits - tooNarrow > CLOSE_ENOUGH_PX; i += 1) {
    const middle = (tooNarrow + fits) / 2;
    const lines = set(middle);
    if (lines.length <= atCap.length) {
      fits = middle;
      fitting = lines;
    } else {
      tooNarrow = middle;
    }
  }

  /* Sized to the longest line rather than to the width that was asked for:
     the search stops within two pixels, and the last line of a balanced
     paragraph is usually shorter than the box it was wrapped in. Checked,
     because balancing is free to re-wrap what it is given: if the trim costs
     a line, the width that was known to hold goes back. */
  const trimmed = set(Math.max(widthOf(fitting), NARROWEST_PX - frame));
  if (trimmed.length > fitting.length) set(fits);
}

export function TipBubble({ text, anchor }: { text: string; anchor: TipAnchor }) {
  const bubble = useRef<HTMLSpanElement>(null);
  const [placed, setPlaced] = useState<TipPlacement | null>(null);

  /* Layout, not effect: the unplaced frame must never reach the screen. */
  useLayoutEffect(() => {
    const node = bubble.current;
    if (!node) return;
    // Back to the stylesheet's cap first: this runs again for the next label,
    // and last time's width would wrap it somewhere of its own.
    node.style.width = '';
    fitToText(node);
    const box = node.getBoundingClientRect();
    setPlaced(placeTip(anchor, box, { width: window.innerWidth, height: window.innerHeight }));
  }, [text, anchor.top, anchor.bottom, anchor.cx]);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <span
      ref={bubble}
      role="tooltip"
      className={`tooltip-bubble${placed?.below ? ' is-below' : ''}`}
      style={
        placed
          ? { top: placed.top, left: placed.left, ['--tip-arrow' as string]: `${placed.arrow}px` }
          : // The measuring frame: on the page, at its natural width, out of
            // sight. `visibility` rather than `display`, which has no box to
            // measure, and off-screen rather than transparent, so nothing
            // flashes in the corner of an eye.
            { top: 0, left: -9999, visibility: 'hidden' }
      }
    >
      {spoken(text)}
    </span>,
    document.body,
  );
}

/* The first sentence carries the weight, the rest explains it: a whole label
   in bold is a label with no shape for the eye to land on. Lines after the
   first read `Name - what it means`, one per line, the name in bold. */
function spoken(text: string): React.ReactNode {
  const [lead, ...lines] = text.split('\n');
  if (lines.length > 0) {
    return (
      <>
        {spoken(lead)}
        {lines.map((line) => {
          const cut = line.indexOf(' - ');
          return (
            <span key={line} className="tip-line">
              {cut < 0 ? line : <><b>{line.slice(0, cut)}</b>{` - ${line.slice(cut + 3)}`}</>}
            </span>
          );
        })}
      </>
    );
  }
  const end = text.search(/[.!?](\s|$)/);
  if (end < 0 || end === text.length - 1) return text;
  return (
    <>
      <b>{text.slice(0, end + 1)}</b>{' '}
      <span className="tip-rest">{text.slice(end + 1).trim()}</span>
    </>
  );
}
